import { DIGIT_MATRIX, DIGIT_DTMF_FREQUENCIES } from 'flow/utils/tone-utils';
import {
    RINGBACK_SETUP,
    BUSY_TONE_SETUP,
    DISCONNECT_TONE_SETUP,
    CALL_WAITING_SETUP,
} from 'flow/utils/tone-utils';
import { getPreferredAudioOutputVolume } from 'flow/utils/browser-utils';
import {
    setOutputDeviceWithIdToElement,
    unsetSinkId,
} from 'flow/utils/media-utils';

class ToneGeneratorClass {
    constructor() {
        try {
            this.context = new (
                window.AudioContext || window.webkitAudioContext
            )();
            this.context.suspend();
        } catch (error) {
            console.warn(
                "Your browser does not support the audio generator and won't generate any sounds",
                error,
            );
        }

        try {
            this.outputDestination =
                this.context.createMediaStreamDestination();
        } catch (_e) {
            this.outputDestination = this.context && this.context.destination;
        }

        this.defaultVolume = 0.3;
        this.phoneFilterFrequency = 800;

        this.outputAudioElement = null;
        this.outputDeviceId = null;

        this.preparingToPlayTone = false;
        this.executingPlayTone = false;

        /* Try to stop the browser from stealing media controls focus from other apps using it.
        This fix will make the play/pause/stop buttons not manipulate ringtones etc, but the
        focus will still be lost for other apps. */
        if ('mediaSession' in navigator) {
            navigator.mediaSession.setActionHandler('play', () => {});
            navigator.mediaSession.setActionHandler('pause', () => {});
            navigator.mediaSession.setActionHandler('stop', () => {});
        }
    }

    setOutputElement = (audioElement) => {
        if (!this.outputDestination || !this.outputDestination.stream) {
            return;
        }
        this.outputAudioElement = audioElement;
        this.outputAudioElement.srcObject = this.outputDestination.stream;
    };

    setOutputDevice = (deviceId) => {
        this.outputDeviceId = deviceId;
    };

    playDigit(digit) {
        const digitFrequencies = this.findDigitIndex(digit);
        this.playDTMF(
            DIGIT_DTMF_FREQUENCIES.getIn(['ROW', digitFrequencies.row]),
            DIGIT_DTMF_FREQUENCIES.getIn(['COL', digitFrequencies.col]),
        );
    }

    playCallWaitingTone = (countryCode) => {
        const callWaitingSetup = CALL_WAITING_SETUP.isDefined(countryCode)
            ? CALL_WAITING_SETUP.get(countryCode).value
            : CALL_WAITING_SETUP.get('DEFAULT').value;
        this.playTone(callWaitingSetup);
    };

    playRingbackTone = (countryCode) => {
        const ringbackSetup = RINGBACK_SETUP.isDefined(countryCode)
            ? RINGBACK_SETUP.get(countryCode).value
            : RINGBACK_SETUP.get('DEFAULT').value;
        this.playTone(ringbackSetup);
    };

    playBusyTone = async (countryCode) => {
        const busyToneSetup = BUSY_TONE_SETUP.isDefined(countryCode)
            ? BUSY_TONE_SETUP.get(countryCode).value
            : BUSY_TONE_SETUP.get('DEFAULT').value;
        await this.playTone(busyToneSetup, false);
    };

    playDisconnectTone = (countryCode) => {
        const disconnectTone = DISCONNECT_TONE_SETUP.isDefined(countryCode)
            ? DISCONNECT_TONE_SETUP.get(countryCode).value
            : DISCONNECT_TONE_SETUP.get('DEFAULT').value;
        this.playTone(disconnectTone, false);
    };

    /*Internal Functions*/

    /**
     * Everytime we change our device or audio element,
     * we want to reestablish this connection.
     * If either is null this can't be done.
     * This occurs at the start of the app for the first value that is being set.
     */

    tryAttachDeviceWithIdToElement = (deviceId, audioElement) => {
        if (deviceId && audioElement) {
            setOutputDeviceWithIdToElement(deviceId, audioElement);
        }
    };

    /*--- Setup Functions ---*/
    setupDtmfNodes = async () => {
        await this.context.resume();
        this.oscillatorDtmf1 = this.context.createOscillator();
        this.oscillatorDtmf2 = this.context.createOscillator();
        this.oscillatorDtmf1.onended = () => (this.oscillatorDtmf1 = null); // This helps safari from breaking by
        this.oscillatorDtmf2.onended = () => (this.oscillatorDtmf2 = null); // not calling stop() when nothing is running.

        this.gainNodeDtmf = this.context.createGain();
        this.gainNodeDtmf.gain.setValueAtTime(
            this.generalVolume(),
            this.context.currentTime,
        );

        this.filterDtmf = this.context.createBiquadFilter();
        this.filterDtmf.type = 'lowpass';
        this.filterDtmf.frequency.setValueAtTime(
            this.phoneFilterFrequency,
            this.context.currentTime,
        );

        this.oscillatorDtmf1.connect(this.gainNodeDtmf);
        this.oscillatorDtmf2.connect(this.gainNodeDtmf);
        this.gainNodeDtmf.connect(this.filterDtmf);
        this.filterDtmf.connect(this.outputDestination);

        this.outputAudioElement.play();
    };

    setupToneNodes = async (volume) => {
        await this.context.resume();
        this.oscillatorTone1 = this.context.createOscillator();
        this.oscillatorTone2 = this.context.createOscillator();

        this.gainNodeTone = this.context.createGain();
        this.gainNodeTone.gain.setValueAtTime(volume, this.context.currentTime);

        this.filterTone = this.context.createBiquadFilter();
        this.filterTone.type = 'lowpass';
        this.filterTone.frequency.setValueAtTime(
            this.phoneFilterFrequency,
            this.context.currentTime,
        );

        this.oscillatorTone1.connect(this.gainNodeTone);
        this.oscillatorTone2.connect(this.gainNodeTone);
        this.gainNodeTone.connect(this.filterTone);
        this.filterTone.connect(this.outputDestination);

        this.outputAudioElement.play();
    };

    generalVolume = () => {
        return this.defaultVolume * getPreferredAudioOutputVolume();
    };

    /*-- DTMF functions --*/
    findDigitIndex = (digit) => {
        let col;
        const row = DIGIT_MATRIX.findKey((row) => {
            col = row.indexOf(digit.toString());
            return col !== -1;
        });
        return { row, col };
    };

    playDTMF = async (frequency1, frequency2) => {
        if (!this.context) {
            return;
        }

        this.stopRunningDtmf();
        await this.setupDtmfNodes();
        this.oscillatorDtmf1.frequency.setValueAtTime(
            frequency1,
            this.context.currentTime,
        );
        this.oscillatorDtmf2.frequency.setValueAtTime(
            frequency2,
            this.context.currentTime,
        );

        this.oscillatorDtmf1.start();
        this.oscillatorDtmf2.start();

        this.oscillatorDtmf1.stop(this.context.currentTime + 0.25);
        this.oscillatorDtmf2.stop(this.context.currentTime + 0.25);
    };

    stopRunningDtmf = () => {
        if (!this.context) {
            return;
        }
        if (this.oscillatorDtmf1) {
            this.oscillatorDtmf1.stop();
            this.oscillatorDtmf1 = null;
        }
        if (this.oscillatorDtmf2) {
            this.oscillatorDtmf2.stop();
            this.oscillatorDtmf2 = null;
        }
    };

    /*-- Tone functions --*/
    playTone = async (toneSpecifications, loop = true) => {
        if (!this.context) {
            return;
        }

        if (this.executingPlayTone) {
            return;
        }
        this.executingPlayTone = true;

        const volume = this.generalVolume();

        this.stopRunningTone(true);

        this.tryAttachDeviceWithIdToElement(
            this.outputDeviceId,
            this.outputAudioElement,
        );
        await this.setupToneNodes(volume);

        this.oscillatorTone1.frequency.setValueAtTime(
            toneSpecifications.frequency1,
            this.context.currentTime,
        );
        this.oscillatorTone2.frequency.setValueAtTime(
            toneSpecifications.frequency2,
            this.context.currentTime,
        );
        this.oscillatorTone1.start();
        this.oscillatorTone2.start();
        this.gainNodeTone.gain.setValueAtTime(0, this.context.currentTime);
        const bufferSource = this.context.createBufferSource();
        bufferSource.buffer = this.createToneWave(
            toneSpecifications.wave,
            volume,
        );
        bufferSource.loop = loop;
        bufferSource.connect(this.gainNodeTone.gain);
        bufferSource.start();
        bufferSource.addEventListener('ended', () => {
            unsetSinkId(this.outputAudioElement);
        });

        this.executingPlayTone = false;
        if (this.requestedStop) {
            this.requestedStop = false;
            this.stopRunningTone();
        }
    };

    stopRunningTone = (stoppingFromPlay) => {
        if (!this.context) {
            return;
        }

        if (this.executingPlayTone && !stoppingFromPlay) {
            this.requestedStop = true;
            return;
        }

        if (this.oscillatorTone1) {
            try {
                this.oscillatorTone1.stop();
            } finally {
                this.oscillatorTone1 = null;
            }
        }
        if (this.oscillatorTone2) {
            try {
                this.oscillatorTone2.stop();
            } finally {
                this.oscillatorTone2 = null;
            }
        }
        unsetSinkId(this.outputAudioElement);
    };

    /*
     * Array with time periods for the tone to make sound, alternating between on and off, starting with on.
     * Ex. [500, 250, 500, 1000] => On for 500ms, off for 250ms, on for 500ms, off for 1000ms, then repeat.
     *
     * An array inside the array is a repeating pattern,
     * where the first value is on-time, the second is off-time
     * and the third is times repeated.
     * [on, off, repeat]
     */
    createToneWave = (toneWaveArray, volume = this.generalVolume()) => {
        const unfoldedWaveArray = this.unfoldArray(toneWaveArray);
        const channels = 1;
        const sampleRate = this.context.sampleRate;
        const waveDurationMS = unfoldedWaveArray.reduce(
            (sum, nextTime) => sum + nextTime,
            0,
        );
        const frameCount = sampleRate * (waveDurationMS / 1000);
        const toneWave = this.context.createBuffer(
            channels,
            frameCount,
            sampleRate,
        );

        const timeToFrameArray = unfoldedWaveArray.map((s) =>
            Math.round((sampleRate / 1000) * s),
        );
        const increasingFrames = timeToFrameArray.reduce((array, frames) => {
            if (array.length > 0) {
                array.push(frames + array[array.length - 1]);
            } else {
                array.push(frames);
            }
            return array;
        }, []);

        const waveData = toneWave.getChannelData(0);

        increasingFrames.forEach((stage, index) => {
            const vol = index % 2 ? 0 : volume;
            if (index === 0) {
                waveData.fill(vol, 0, stage);
            } else {
                waveData.fill(vol, increasingFrames[index - 1], stage);
            }
        });
        return toneWave;
    };

    /*
     * Unfold repeating parts of a wave array and return
     * a correct array with only alternating on- and off-times.
     */
    unfoldArray = (waveArray) => {
        const unfoldedPart = [];
        waveArray.forEach((part) => {
            if (part instanceof Object) {
                for (let i = 0; i < part[2]; i++) {
                    unfoldedPart.push(part[0], part[1]);
                }
            } else {
                unfoldedPart.push(part);
            }
        });
        return unfoldedPart;
    };
}

export const ToneGenerator = new ToneGeneratorClass();
